import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PlatformSettingsState {
  pricingEnabled: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

let cachedPricingEnabled: boolean | null = null;
let cacheTime = 0;
let inflight: Promise<boolean> | null = null;
const CACHE_MS = 60_000;

/**
 * Reads site-wide platform settings. `pricingEnabled` controls whether
 * /pricing, upgrade prompts, and tier caps are surfaced anywhere in the app.
 * When disabled (pre-launch mode), the app behaves as if everyone has full access.
 */
export function usePlatformSettings(): PlatformSettingsState {
  const [pricingEnabled, setPricingEnabled] = useState<boolean>(
    cachedPricingEnabled ?? false,
  );
  const [loading, setLoading] = useState(cachedPricingEnabled === null);

  const load = async () => {
    const now = Date.now();
    if (cachedPricingEnabled !== null && now - cacheTime < CACHE_MS) {
      setPricingEnabled(cachedPricingEnabled);
      setLoading(false);
      return;
    }
    // Dedupe concurrent loads across all hook instances so multiple
    // components mounting on the same page issue a single network request.
    if (!inflight) {
      inflight = (async () => {
        const { data } = await supabase
          .from("platform_settings")
          .select("pricing_enabled")
          .eq("id", 1)
          .maybeSingle();
        const value = !!data?.pricing_enabled;
        cachedPricingEnabled = value;
        cacheTime = Date.now();
        return value;
      })().finally(() => {
        inflight = null;
      });
    }
    const value = await inflight;
    setPricingEnabled(value);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    pricingEnabled,
    loading,
    refresh: async () => {
      cachedPricingEnabled = null;
      await load();
    },
  };
}